import { FileText, ChevronDown, ChevronUp, MapPin } from 'lucide-react';
import { useState } from 'react';
import type { ChatMessage, SourceCitation } from '../../types';

interface MessageBubbleProps {
  message: ChatMessage;
  onSourceClick?: (source: SourceCitation) => void;
}

export function MessageBubble({ message, onSourceClick }: MessageBubbleProps) {
  const [sourcesExpanded, setSourcesExpanded] = useState(false); // Collapsed by default
  const isUser = message.role === 'user';
  const hasSources = message.sources && message.sources.length > 0;
  const hasProtocolContext = !isUser && message.protocolContext;

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600 px-4 py-2.5 text-white md:max-w-[75%]">
          <div className="whitespace-pre-wrap break-words text-[15px] leading-relaxed md:text-sm">
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  // Assistant: full-width plain block, no bubble chrome
  return (
    <div className="min-w-0">
      {/* Protocol context banner */}
      {hasProtocolContext && (
        <div className="mb-2 inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs dark:bg-primary-900/20 dark:border-primary-800">
          <MapPin size={12} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
          <span className="truncate text-primary-700 dark:text-primary-300">
            {message.protocolContext!.matched_from_message && (
              <span className="text-primary-500 mr-1 dark:text-primary-400">Detected:</span>
            )}
            <span className="font-medium">{message.protocolContext!.protocol_number}</span>
            {message.protocolContext!.site_name && (
              <span className="text-primary-500 dark:text-primary-400"> at {message.protocolContext!.site_name}</span>
            )}
          </span>
        </div>
      )}

      {message.isLoading ? (
        <div className="flex items-center gap-2 py-1">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
        </div>
      ) : (
        <div className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-gray-800 dark:text-gray-100 md:text-sm">
          {message.content}
        </div>
      )}

      {/* Sources */}
      {hasSources && !message.isLoading && (
        <div className="mt-2">
          <button
            onClick={() => setSourcesExpanded(!sourcesExpanded)}
            className="flex min-h-[36px] items-center gap-1 rounded-md text-xs text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FileText size={12} />
            <span>{message.sources!.length} source{message.sources!.length > 1 ? 's' : ''}</span>
            {sourcesExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>

          {sourcesExpanded && (
            <div className="mt-1 space-y-2">
              {message.sources!.map((source, index) => (
                <SourceCard key={index} source={source} onClick={onSourceClick} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SourceCardProps {
  source: SourceCitation;
  onClick?: (source: SourceCitation) => void;
}

function SourceCard({ source, onClick }: SourceCardProps) {
  const isClickable = onClick !== undefined;

  return (
    <button
      type="button"
      onClick={() => onClick?.(source)}
      disabled={!isClickable}
      className={`
        w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-left dark:bg-gray-800 dark:border-gray-700
        ${isClickable ? 'cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-colors duration-150 dark:hover:bg-primary-900/20 dark:hover:border-primary-700' : ''}
      `}
    >
      <div className="flex items-start gap-2">
        <FileText size={14} className="text-primary-600 mt-0.5 flex-shrink-0 dark:text-primary-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
              {source.document_title}
            </p>
            {source.document_type && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 uppercase dark:bg-primary-900/40 dark:text-primary-300">
                {source.document_type}
              </span>
            )}
            {isClickable && (
              <span className="text-[10px] text-primary-600 ml-auto dark:text-primary-400">
                Click to view
              </span>
            )}
          </div>
          {source.page_number && (
            <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400">
              Page {source.page_number}
            </p>
          )}
          <p className="text-xs text-gray-600 mt-1 line-clamp-2 dark:text-gray-300">
            {source.content_preview}
          </p>
        </div>
      </div>
    </button>
  );
}
